import React, { useMemo, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import { loadAssignmentsMap, getCarForDriverByDate } from "../../earningsImport/assignmentUtils";
import { normalizePlateDigits } from "../../earningsImport/runningExpensesParser";

const ALL_INCOME_COMPANIES = "__all__";

const COMPANIES = [
  {
    value: "Comfort Line Luxury Motor Vehicles Services LLC",
    label: "Comfort Line Luxury",
  },
  {
    value: "Lolia Limousine for Luxury Motor Vehicles Services LLC",
    label: "Lolia Limousine",
  },
];

const money = (v) =>
  Number(v || 0).toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const n2 = (v) => Math.round((Number(v || 0) + Number.EPSILON) * 100) / 100;

function toIsoDate(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}

function parseIsoDate(s) {
  const [y, m, d] = String(s || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function enumerateDates(startDate, endDate) {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  if (!start || !end || end < start) return [];
  const out = [];
  const d = new Date(start);
  while (d <= end) {
    out.push(toIsoDate(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

function enumerateMonths(startDate, endDate) {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  if (!start || !end || end < start) return [];
  const d = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  const out = [];
  while (d <= last) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    d.setMonth(d.getMonth() + 1);
  }
  return out;
}

function dateInRange(date, start, end) {
  return String(date || "") >= String(start || "") && String(date || "") <= String(end || "");
}

function normalizeCompany(value) {
  return String(value || "").trim().toLowerCase();
}

function companyLabel(value) {
  if (value === ALL_INCOME_COMPANIES) return "All Income Companies";
  return COMPANIES.find((c) => normalizeCompany(c.value) === normalizeCompany(value))?.label || value || "-";
}

function matchesIncomeCompany(rowCompany, selectedCompany) {
  if (!selectedCompany || selectedCompany === ALL_INCOME_COMPANIES) return true;
  return normalizeCompany(rowCompany) === normalizeCompany(selectedCompany);
}

function lastDayOfMonth(month) {
  const [year, monthNo] = String(month || "").split("-").map(Number);
  if (!year || !monthNo) return 28;
  return new Date(year, monthNo, 0).getDate();
}

function yangoAllocationDate(month, week) {
  const match = String(week || "").toLowerCase().match(/week\s*(\d+)/);
  const weekNo = Number(match?.[1] || 0);
  if (!month || weekNo < 1 || weekNo > 5) return "";

  // Management-report convention only. Existing Yango / Car Sheet logic is untouched.
  const day = weekNo <= 4 ? weekNo * 7 : lastDayOfMonth(month);
  return `${month}-${String(day).padStart(2, "0")}`;
}

async function loadVehicleMap() {
  const snap = await getDocs(collection(db, "vehicles"));
  const byNumber = {};
  snap.forEach((d) => {
    const row = d.data() || {};
    const number = String(row.vehicleNumber || "").trim();
    if (!number) return;
    byNumber[number] = {
      id: d.id,
      carNumber: number,
      carType: row.vehicleType || row.makeModel || row.model || "",
      carCompany: row.company || "",
      // Current vehicle master keeps the primary/main driver. This is useful
      // for a valid two-driver car where both assignment records are still
      // open: the pair must stay on the same car and POS must not be treated
      // as unallocated merely because both end dates are null.
      assignedDriverId: row.assignedDriverId || "",
      assignedDriverName: row.assignedDriverName || "",
    };
  });
  return byNumber;
}

async function loadDriverMasterMap() {
  const snap = await getDocs(collection(db, "drivers"));
  const map = {};
  snap.forEach((d) => {
    const row = d.data() || {};
    map[d.id] = {
      fullName: String(row.fullName || row.name || "").trim(),
      company: row.company || "",
    };
  });
  return map;
}

async function loadDailyRideHailing(startDate, endDate, incomeCompany) {
  const out = [];
  for (const date of enumerateDates(startDate, endDate)) {
    const snap = await getDocs(collection(db, "dailyRideHailingEarnings", date, "records"));
    snap.forEach((d) => {
      const row = d.data() || {};
      const rowIncomeCompany = row.incomeCompany || row.company || "";
      if (!matchesIncomeCompany(rowIncomeCompany, incomeCompany)) return;
      out.push({ id: d.id, ...row, incomeCompany: rowIncomeCompany });
    });
  }
  return out;
}

async function loadDailyBookingPortal(startDate, endDate, incomeCompany) {
  const out = [];
  for (const date of enumerateDates(startDate, endDate)) {
    const snap = await getDocs(collection(db, "dailyBookingPortalEarnings", date, "records"));
    snap.forEach((d) => {
      const row = d.data() || {};
      const rowIncomeCompany = row.incomeCompany || row.company || "";
      if (!matchesIncomeCompany(rowIncomeCompany, incomeCompany)) return;
      out.push({ id: d.id, ...row, incomeCompany: rowIncomeCompany });
    });
  }
  return out;
}

async function loadPOS(startDate, endDate, incomeCompany) {
  // POS remains the existing upload/data source. Company selection follows the
  // company already stored on the POS record; existing POS/Car Sheet logic is untouched.
  const out = [];
  for (const month of enumerateMonths(startDate, endDate)) {
    const snap = await getDocs(collection(db, "posUploads", month, "records"));
    snap.forEach((d) => {
      const row = d.data() || {};
      if (!dateInRange(row.date, startDate, endDate)) return;
      const rowIncomeCompany = row.company || row.carCompany || "";
      if (!matchesIncomeCompany(rowIncomeCompany, incomeCompany)) return;
      out.push({ id: d.id, ...row, incomeCompany: rowIncomeCompany });
    });
  }
  return out;
}

function expenseBucket(category) {
  const key = String(category || "").trim().toUpperCase().replace(/\s+/g, " ");
  if (["PETROL", "ENOC", "FUEL"].includes(key)) return "enocExpense";
  if (["TRAFFIC FINE", "TRAFFIC FINES", "FINE", "FINES"].includes(key)) return "trafficFineExpense";
  return "";
}

async function loadReportExpenses(startDate, endDate) {
  const out = [];
  for (const month of enumerateMonths(startDate, endDate)) {
    const snap = await getDocs(collection(db, "carRunningExpenses", month, "rows"));
    snap.forEach((d) => {
      const row = d.data() || {};
      const date = row.tripDate || row.date || "";
      if (!dateInRange(date, startDate, endDate)) return;
      const bucket = expenseBucket(row.category || row.expenseType);
      if (!bucket) return;
      out.push({ id: d.id, ...row, reportExpenseDate: date, expenseBucket: bucket });
    });
  }
  return out;
}

function resolveExpenseCarNumber(row, vehicleMap) {
  const directCandidates = [
    row.matchedCarNumber,
    row.carNumber,
    row.vehicleNumber,
  ].map((v) => String(v || "").trim()).filter(Boolean);

  for (const candidate of directCandidates) {
    if (vehicleMap[candidate]) return candidate;
  }

  const targetDigits = normalizePlateDigits(
    row.plateDigits || row.plate || row.carNumber || row.vehicleNumber || row.matchedCarNumber
  );
  if (!targetDigits) return directCandidates[0] || "";

  const found = Object.values(vehicleMap || {}).find(
    (meta) => normalizePlateDigits(meta?.carNumber) === targetDigits
  );
  return found?.carNumber || directCandidates[0] || String(row.plate || "").trim();
}

async function loadYangoAllocated(startDate, endDate, incomeCompany, assignmentsMap, vehicleMap, driverMasterMap) {
  const months = enumerateMonths(startDate, endDate);
  const targetKeys = new Map();

  months.forEach((month) => {
    ["week1", "week2", "week3", "week4", "week5"].forEach((week) => {
      const allocationDate = yangoAllocationDate(month, week);
      if (allocationDate && dateInRange(allocationDate, startDate, endDate)) {
        targetKeys.set(`${month}__${week}`, allocationDate);
      }
    });
  });

  if (!targetKeys.size) return { rows: [], allocatedWeeks: [] };

  const rows = [];
  const allocatedWeeks = new Set();
  const driverParents = await getDocs(collection(db, "driverWeeklyEarnings"));

  for (const parent of driverParents.docs) {
    const driverId = parent.id;
    const uploads = await getDocs(collection(db, "driverWeeklyEarnings", driverId, "uploads"));

    uploads.forEach((u) => {
      const row = u.data() || {};
      const platform = String(row.platform || "").toLowerCase();
      const week = String(row.week || "").toLowerCase();
      const month = String(row.month || "");
      const allocationDate = targetKeys.get(`${month}__${week}`);

      if (platform !== "yango" || !allocationDate) return;
      if (!matchesIncomeCompany(row.company, incomeCompany)) return;

      const assignment = getCarForDriverByDate({
        driverId,
        date: allocationDate,
        assignmentsMap,
      });
      const carNumber = assignment?.vehicleNumber || "UNASSIGNED";
      const meta = vehicleMap[carNumber] || {};

      rows.push({
        driverId,
        driverName: driverMasterMap?.[driverId]?.fullName || assignment?.driverName || row.driverName || "",
        vehicleNumber: carNumber,
        vehicleType: meta.carType || assignment?.vehicleType || "",
        carCompany: meta.carCompany || assignment?.company || "",
        bankIncome: Number(row.bankIncome || 0),
        cashIncome: Number(row.cashIncome || 0),
        incomeCompany: row.company || "",
        month,
        week,
        allocationDate,
      });

      allocatedWeeks.add(`${month} ${week.toUpperCase()} -> ${allocationDate}`);
    });
  }

  return { rows, allocatedWeeks: Array.from(allocatedWeeks).sort() };
}

function activeAssignmentsForCarDate(assignmentsMap, carNumber, date) {
  const target = parseIsoDate(date);
  if (!target || !carNumber) return [];
  target.setHours(0, 0, 0, 0);
  const out = [];

  Object.entries(assignmentsMap || {}).forEach(([driverId, assignments]) => {
    (assignments || []).forEach((a) => {
      if (String(a.vehicleNumber || "") !== String(carNumber)) return;
      const start = new Date(a.startDate);
      const end = a.endDate ? new Date(a.endDate) : new Date(9999, 0, 1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      if (target >= start && target <= end) {
        out.push({
          ...a,
          driverId,
          driverName: a.driverName || "",
          startDate: new Date(a.startDate),
          endDate: a.endDate ? new Date(a.endDate) : null,
        });
      }
    });
  });

  // A duplicate assignment document for the same driver/car/date must not make
  // the POS resolver think two different drivers are present.
  const byDriver = new Map();
  out.forEach((a) => {
    const existing = byDriver.get(a.driverId);
    if (!existing) {
      byDriver.set(a.driverId, a);
      return;
    }
    const existingEnd = existing.endDate ? existing.endDate.getTime() : Number.POSITIVE_INFINITY;
    const newEnd = a.endDate ? a.endDate.getTime() : Number.POSITIVE_INFINITY;
    if (newEnd > existingEnd) byDriver.set(a.driverId, a);
  });
  return Array.from(byDriver.values());
}

function choosePosDriver(activeAssignments, vehicleMeta = {}) {
  if (!activeAssignments.length) return { assignment: null, reason: "no-assignment" };
  if (activeAssignments.length === 1) return { assignment: activeAssignments[0], reason: "single-driver" };

  // IMPORTANT: overlapping assignments on the SAME car are valid in this
  // system. Car Sheets merge those assignments into one car period. Therefore
  // two drivers who are both still assigned to the same car must NOT create an
  // unallocated POS warning just because both endDate values are null.

  // 1) First honour the business rule already agreed for a handover/overlap:
  // if one driver stops and the other continues on the car, POS stays with the
  // driver who continues longer.
  const scored = activeAssignments.map((a) => ({
    a,
    endScore: a.endDate ? a.endDate.getTime() : Number.POSITIVE_INFINITY,
  }));
  const maxEnd = Math.max(...scored.map((x) => x.endScore));
  const longest = scored.filter((x) => x.endScore === maxEnd);
  if (longest.length === 1) {
    return { assignment: longest[0].a, reason: "continuing-driver" };
  }

  // From here the drivers continue to the same end date (most commonly both
  // are open-ended). That is a valid two-driver car, not an error. For the
  // driver-wise cash report, keep the shared car POS with the car's main/base
  // driver so the POS is adjusted instead of being left unallocated.
  const tiedAssignments = longest.map((x) => x.a);
  const normalizedRole = (a) =>
    String(a?.driverRole || a?.assignmentRole || a?.role || "").trim().toLowerCase();

  // 2) Prefer the explicitly stored Main Driver when exactly one exists.
  const mains = tiedAssignments.filter((a) => normalizedRole(a) === "main");
  if (mains.length === 1) {
    return {
      assignment: mains[0],
      reason: "main-driver",
      sharedWith: activeAssignments.filter((a) => a.driverId !== mains[0].driverId),
    };
  }

  // 3) Fallback to the vehicle's primary assigned driver, but only if that
  // driver is actually active on the POS transaction date.
  const vehicleMainId = String(vehicleMeta?.assignedDriverId || "");
  if (vehicleMainId) {
    const vehicleMain = tiedAssignments.find((a) => String(a.driverId || "") === vehicleMainId);
    if (vehicleMain) {
      return {
        assignment: vehicleMain,
        reason: "vehicle-main-driver",
        sharedWith: activeAssignments.filter((a) => a.driverId !== vehicleMain.driverId),
      };
    }
  }

  // 4) Older assignment data may not have a driverRole. When both drivers are
  // still on the car, use the earlier-starting/base driver as the POS holder.
  // The two drivers still remain separate driver rows and both keep this car in
  // Cars Used; this rule only decides where the shared car POS is deducted.
  const sortedByStart = [...tiedAssignments].sort((a, b) => {
    const aStart = a.startDate ? a.startDate.getTime() : Number.POSITIVE_INFINITY;
    const bStart = b.startDate ? b.startDate.getTime() : Number.POSITIVE_INFINITY;
    if (aStart !== bStart) return aStart - bStart;
    return String(a.driverId || "").localeCompare(String(b.driverId || ""));
  });

  if (sortedByStart.length) {
    return {
      assignment: sortedByStart[0],
      reason: "ongoing-two-driver-car",
      sharedWith: activeAssignments.filter((a) => a.driverId !== sortedByStart[0].driverId),
    };
  }

  return { assignment: null, reason: "no-assignment" };
}

function matchesCarCompany(carCompany, filter) {
  if (!filter || filter === "all") return true;
  return normalizeCompany(carCompany) === normalizeCompany(filter);
}

function ensureDriver(map, driverId, sourceName = "", driverMasterMap = {}) {
  const masterName = driverId ? String(driverMasterMap?.[driverId]?.fullName || "").trim() : "";
  const resolvedName = masterName || sourceName || "Unknown Driver";
  const key = driverId || `name:${resolvedName}`;

  if (!map[key]) {
    map[key] = {
      driverId: driverId || "",
      driverName: resolvedName,
      cars: new Map(),
      uberBank: 0,
      uberCash: 0,
      boltBank: 0,
      boltCash: 0,
      yangoBank: 0,
      yangoCash: 0,
      bookingPortalBank: 0,
      bookingPortalCash: 0,
      pos: 0,
      enocExpense: 0,
      trafficFineExpense: 0,
      posAutoSingle: 0,
      posAutoContinuing: 0,
    };
  } else if (masterName) {
    map[key].driverName = masterName;
  }

  return map[key];
}

function addCar(driver, carNumber, carType = "", carCompany = "") {
  const number = String(carNumber || "UNASSIGNED");
  const key = number;
  const existing = driver.cars.get(key);
  if (!existing) {
    driver.cars.set(key, { carNumber: number, carType: carType || "", carCompany: carCompany || "" });
  } else {
    if (!existing.carType && carType) existing.carType = carType;
    if (!existing.carCompany && carCompany) existing.carCompany = carCompany;
  }
}

function carDisplay(driver) {
  return Array.from(driver.cars.values())
    .sort((a, b) => String(a.carNumber).localeCompare(String(b.carNumber), undefined, { numeric: true }))
    .map((c) => (c.carType ? `${c.carNumber} (${c.carType})` : c.carNumber))
    .join(" / ");
}

function sortDriverRowsBySharedCars(rows) {
  if (!Array.isArray(rows) || rows.length <= 1) return rows || [];

  const parent = rows.map((_, i) => i);
  const find = (x) => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  // Drivers who used the same car anywhere inside the selected range belong to
  // one visual group. This keeps two active drivers on the same car next to
  // each other even though the report itself remains one row per driver.
  const carToIndexes = new Map();
  rows.forEach((row, index) => {
    Array.from(row.cars?.values?.() || []).forEach((car) => {
      const number = String(car?.carNumber || "").trim();
      if (!number || number === "UNASSIGNED") return;
      if (!carToIndexes.has(number)) carToIndexes.set(number, []);
      carToIndexes.get(number).push(index);
    });
  });

  carToIndexes.forEach((indexes) => {
    if (indexes.length < 2) return;
    for (let i = 1; i < indexes.length; i += 1) union(indexes[0], indexes[i]);
  });

  const components = new Map();
  rows.forEach((row, index) => {
    const root = find(index);
    if (!components.has(root)) components.set(root, []);
    components.get(root).push(row);
  });

  const natural = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
  const componentData = Array.from(components.values()).map((members) => {
    const cars = new Set();
    members.forEach((row) => {
      Array.from(row.cars?.values?.() || []).forEach((car) => {
        const number = String(car?.carNumber || "").trim();
        if (number && number !== "UNASSIGNED") cars.add(number);
      });
    });
    const sortedCars = Array.from(cars).sort(natural);
    members.sort((a, b) => {
      // Within a shared-car group keep drivers with the same car beside each other.
      const aCars = Array.from(a.cars?.keys?.() || []).filter((x) => x !== "UNASSIGNED").sort(natural);
      const bCars = Array.from(b.cars?.keys?.() || []).filter((x) => x !== "UNASSIGNED").sort(natural);
      const shared = aCars.find((car) => bCars.includes(car));
      if (shared) return natural(a.driverName, b.driverName);
      const carCmp = natural(aCars[0] || "ZZZZ", bCars[0] || "ZZZZ");
      return carCmp || natural(a.driverName, b.driverName);
    });
    return {
      members,
      sortKey: sortedCars[0] || `ZZZZ-${members[0]?.driverName || ""}`,
    };
  });

  componentData.sort((a, b) => {
    const byCar = natural(a.sortKey, b.sortKey);
    return byCar || natural(a.members[0]?.driverName || "", b.members[0]?.driverName || "");
  });

  return componentData.flatMap((group, groupIndex) =>
    group.members.map((row) => ({ ...row, displayGroupIndex: groupIndex }))
  );
}

function seedAssignedDriversForCarReport({
  drivers,
  assignmentsMap,
  vehicleMap,
  driverMasterMap,
  carCompanyFilter,
  startDate,
  endDate,
}) {
  const rangeStart = parseIsoDate(startDate);
  const rangeEnd = parseIsoDate(endDate);
  if (!rangeStart || !rangeEnd) return;
  rangeStart.setHours(0, 0, 0, 0);
  rangeEnd.setHours(23, 59, 59, 999);

  Object.entries(assignmentsMap || {}).forEach(([driverId, assignments]) => {
    (assignments || []).forEach((assignment) => {
      const assignmentStart = new Date(assignment.startDate);
      const assignmentEnd = assignment.endDate ? new Date(assignment.endDate) : new Date(9999, 0, 1);
      assignmentStart.setHours(0, 0, 0, 0);
      assignmentEnd.setHours(23, 59, 59, 999);

      // Include any assignment that overlaps at least one day in the selected
      // report range. This intentionally seeds drivers even when Uber/Bolt/Yango
      // income is zero, so an assigned car never disappears from the report.
      if (assignmentStart > rangeEnd || assignmentEnd < rangeStart) return;

      const carNumber = String(assignment.vehicleNumber || "").trim();
      if (!carNumber) return;
      const meta = vehicleMap[carNumber] || {};
      const carType = meta.carType || assignment.vehicleType || "";
      const carCompany = meta.carCompany || assignment.company || "";
      if (!matchesCarCompany(carCompany, carCompanyFilter)) return;

      const driver = ensureDriver(
        drivers,
        driverId,
        assignment.driverName || "",
        driverMasterMap
      );
      addCar(driver, carNumber, carType, carCompany);
    });
  });
}

function buildReport({
  dailyRows,
  bookingPortalRows,
  posRows,
  yangoRows,
  expenseRows,
  assignmentsMap,
  vehicleMap,
  driverMasterMap,
  carCompanyFilter,
  reportBy,
  startDate,
  endDate,
}) {
  const drivers = {};
  const warnings = [];
  let unallocatedPos = 0;
  const unallocatedExpenses = {
    enocExpense: 0,
    trafficFineExpense: 0,
  };

  // In Company Cars mode the report is also an assignment/status report.
  // Seed every driver whose assignment overlaps the selected range so cars
  // with zero ride-hailing income still appear with 0.00 values. Platform
  // Account mode remains source-led because a zero-income assignment has no
  // platform-account owner to attribute.
  if (reportBy === "cars") {
    seedAssignedDriversForCarReport({
      drivers,
      assignmentsMap,
      vehicleMap,
      driverMasterMap,
      carCompanyFilter,
      startDate,
      endDate,
    });
  }

  dailyRows.forEach((row) => {
    const assignment = getCarForDriverByDate({
      driverId: row.driverId,
      date: row.reportDate,
      assignmentsMap,
    });

    const carNumber = assignment?.vehicleNumber || "UNASSIGNED";
    const meta = vehicleMap[carNumber] || {};
    const carType = meta.carType || assignment?.vehicleType || "";
    const carCompany = meta.carCompany || assignment?.company || "";
    if (!matchesCarCompany(carCompany, carCompanyFilter)) return;

    const driver = ensureDriver(
      drivers,
      row.driverId,
      assignment?.driverName || row.driverName || "",
      driverMasterMap
    );
    addCar(driver, carNumber, carType, carCompany);

    const platform = String(row.platform || "").toLowerCase();
    if (platform === "uber") {
      driver.uberBank += Number(row.bankIncome || 0);
      driver.uberCash += Number(row.cashIncome || 0);
    } else if (platform === "bolt") {
      driver.boltBank += Number(row.bankIncome || 0);
      driver.boltCash += Number(row.cashIncome || 0);
    }
  });

  yangoRows.forEach((row) => {
    const meta = vehicleMap[row.vehicleNumber] || {};
    const carType = meta.carType || row.vehicleType || "";
    const carCompany = meta.carCompany || row.carCompany || "";
    if (!matchesCarCompany(carCompany, carCompanyFilter)) return;

    const driver = ensureDriver(drivers, row.driverId, row.driverName || "", driverMasterMap);
    addCar(driver, row.vehicleNumber || "UNASSIGNED", carType, carCompany);
    driver.yangoBank += Number(row.bankIncome || 0);
    driver.yangoCash += Number(row.cashIncome || 0);
  });

  (bookingPortalRows || []).forEach((row) => {
    const assignment = getCarForDriverByDate({
      driverId: row.driverId,
      date: row.reportDate,
      assignmentsMap,
    });

    const carNumber = assignment?.vehicleNumber || "UNASSIGNED";
    const meta = vehicleMap[carNumber] || {};
    const carType = meta.carType || assignment?.vehicleType || "";
    const carCompany = meta.carCompany || assignment?.company || "";
    if (!matchesCarCompany(carCompany, carCompanyFilter)) return;

    const driver = ensureDriver(
      drivers,
      row.driverId,
      assignment?.driverName || row.driverName || "",
      driverMasterMap
    );
    addCar(driver, carNumber, carType, carCompany);
    driver.bookingPortalBank += Number(row.bankIncome || 0);
    driver.bookingPortalCash += Number(row.cashIncome || 0);
  });

  posRows.forEach((row) => {
    const carNumber = String(row.carNumber || "UNASSIGNED");
    const meta = vehicleMap[carNumber] || {};
    const carType = meta.carType || row.vehicleType || "";
    const carCompany = meta.carCompany || row.carCompany || "";
    if (!matchesCarCompany(carCompany, carCompanyFilter)) return;

    const amount = Number(row.amount || 0);
    const active = activeAssignmentsForCarDate(assignmentsMap, carNumber, row.date);
    const choice = choosePosDriver(active, meta);

    if (!choice.assignment) {
      unallocatedPos += amount;
      warnings.push(`${row.date} car ${carNumber}: AED ${money(amount)} POS has no matching driver assignment.`);
      return;
    }

    const a = choice.assignment;
    const driver = ensureDriver(drivers, a.driverId, a.driverName || "", driverMasterMap);
    addCar(driver, carNumber, carType || a.vehicleType || "", carCompany || a.company || "");
    driver.pos += amount;
    if (["continuing-driver", "main-driver", "vehicle-main-driver", "ongoing-two-driver-car", "base-driver"].includes(choice.reason)) {
      driver.posAutoContinuing += amount;
    } else {
      driver.posAutoSingle += amount;
    }
  });

  // Expenses are car/date based. For this driver-focused management report,
  // use the same assignment resolver as POS so one-driver, handover, and valid
  // ongoing two-driver cars are handled consistently. Existing Car Sheets are
  // not changed by this reporting allocation.
  (expenseRows || []).forEach((row) => {
    const bucket = row.expenseBucket || expenseBucket(row.category || row.expenseType);
    if (!bucket) return;

    const carNumber = resolveExpenseCarNumber(row, vehicleMap);
    const meta = vehicleMap[carNumber] || {};
    const carType = meta.carType || row.vehicleType || "";
    const carCompany = meta.carCompany || row.carCompany || "";
    if (!matchesCarCompany(carCompany, carCompanyFilter)) return;

    const amount = Number(row.amount || 0);
    if (!Number.isFinite(amount) || amount === 0) return;
    const expenseDate = row.reportExpenseDate || row.tripDate || row.date || "";
    const active = activeAssignmentsForCarDate(assignmentsMap, carNumber, expenseDate);
    const choice = choosePosDriver(active, meta);

    if (!choice.assignment) {
      unallocatedExpenses[bucket] += amount;
      warnings.push(`${expenseDate} car ${carNumber || row.plate || "-"}: AED ${money(amount)} ${String(row.category || "expense")} has no matching driver assignment.`);
      return;
    }

    const a = choice.assignment;

    // In Platform Account mode an expense has no platform-account owner of its
    // own. Only attach it when that driver is already present in the selected
    // platform-account report, preventing unrelated fleet expenses from appearing.
    if (reportBy === "account" && !drivers[a.driverId]) return;

    const driver = ensureDriver(drivers, a.driverId, a.driverName || "", driverMasterMap);
    addCar(driver, carNumber || a.vehicleNumber || "UNASSIGNED", carType || a.vehicleType || "", carCompany || a.company || "");
    driver[bucket] += amount;
  });

  const driverRows = sortDriverRowsBySharedCars(
    Object.values(drivers).map((driver) => {
      const rideHailingBank = driver.uberBank + driver.boltBank + driver.yangoBank;
      const rideHailingCash = driver.uberCash + driver.boltCash + driver.yangoCash;
      const totalCashCollected = rideHailingCash + driver.bookingPortalCash;
      return {
        ...driver,
        carsUsed: carDisplay(driver) || "-",
        rideHailingBank,
        rideHailingCash,
        totalCashCollected,
        totalBank: rideHailingBank + driver.bookingPortalBank + driver.pos,
        remainingCash: totalCashCollected - driver.pos,
      };
    })
  );

  driverRows.forEach((d) => {
    if (d.remainingCash < -0.01) {
      warnings.push(`${d.driverName}: POS exceeds total cash collected (ride-hailing + Booking Portal) by AED ${money(Math.abs(d.remainingCash))}.`);
    }
  });

  return { driverRows, warnings, unallocatedPos, unallocatedExpenses };
}

function safeFileName(value) {
  return String(value || "report").replace(/[<>:"/\\|?*]+/g, "").trim();
}

function excelApplyHeader(row) {
  row.font = { bold: true, color: { argb: "FFFFFFFF" } };
  row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } };
  row.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  row.height = 32;
  row.eachCell((cell) => {
    cell.border = {
      top: { style: "thin", color: { argb: "FF9FBAD0" } },
      bottom: { style: "thin", color: { argb: "FF9FBAD0" } },
      left: { style: "thin", color: { argb: "FF9FBAD0" } },
      right: { style: "thin", color: { argb: "FF9FBAD0" } },
    };
  });
}

function excelApplyDataRow(row, fillArgb) {
  row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillArgb } };
  row.alignment = { vertical: "middle" };
  row.eachCell((cell) => {
    cell.border = {
      top: { style: "hair", color: { argb: "FFD5DEE7" } },
      bottom: { style: "hair", color: { argb: "FFD5DEE7" } },
      left: { style: "hair", color: { argb: "FFD5DEE7" } },
      right: { style: "hair", color: { argb: "FFD5DEE7" } },
    };
  });
}

export default function MDDailyIncomeReport() {
  const today = toIsoDate(new Date());
  const [reportBy, setReportBy] = useState("cars");
  const [company, setCompany] = useState(COMPANIES[0].value);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [driverRows, setDriverRows] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [yangoWeeks, setYangoWeeks] = useState([]);
  const [unallocatedPos, setUnallocatedPos] = useState(0);
  const [unallocatedExpenses, setUnallocatedExpenses] = useState({
    enocExpense: 0,
    trafficFineExpense: 0,
  });
  const [needsRegenerate, setNeedsRegenerate] = useState(false);

  const reportByLabel = reportBy === "cars" ? "By Company Cars" : "By Platform Account";
  const selectedCompanyLabel = companyLabel(company);
  const scopeDescription = reportBy === "cars"
    ? `Showing all income generated while using ${selectedCompanyLabel} cars. Driver company and Uber/Bolt/Yango/Booking Portal account company do not restrict this view.`
    : `Showing income uploaded under ${selectedCompanyLabel}'s Uber/Bolt/Yango/Booking Portal/POS account data, regardless of which company's car was used.`;

  const detailTotals = useMemo(() => {
    const totals = driverRows.reduce(
      (a, d) => {
        a.uberBank += d.uberBank;
        a.uberCash += d.uberCash;
        a.boltBank += d.boltBank;
        a.boltCash += d.boltCash;
        a.yangoBank += d.yangoBank;
        a.yangoCash += d.yangoCash;
        a.bookingPortalBank += Number(d.bookingPortalBank || 0);
        a.bookingPortalCash += Number(d.bookingPortalCash || 0);
        a.totalCashCollected += Number(d.totalCashCollected || 0);
        a.assignedPos += Number(d.pos || 0);
        a.rideHailingBank += Number(d.rideHailingBank || 0);
        a.rideHailingCash += Number(d.rideHailingCash || 0);
        a.enocExpense += Number(d.enocExpense || 0);
        a.trafficFineExpense += Number(d.trafficFineExpense || 0);
        return a;
      },
      {
        uberBank: 0,
        uberCash: 0,
        boltBank: 0,
        boltCash: 0,
        yangoBank: 0,
        yangoCash: 0,
        bookingPortalBank: 0,
        bookingPortalCash: 0,
        totalCashCollected: 0,
        assignedPos: 0,
        rideHailingBank: 0,
        rideHailingCash: 0,
        enocExpense: 0,
          trafficFineExpense: 0,
      }
    );
    totals.pos = totals.assignedPos + Number(unallocatedPos || 0);
    totals.totalBank = totals.rideHailingBank + totals.bookingPortalBank + totals.pos;
    totals.remainingCash = totals.totalCashCollected - totals.pos;
    totals.enocExpense += Number(unallocatedExpenses?.enocExpense || 0);
    totals.trafficFineExpense += Number(unallocatedExpenses?.trafficFineExpense || 0);
    return totals;
  }, [driverRows, unallocatedPos, unallocatedExpenses]);

  const invalidateGeneratedReport = () => {
    setDriverRows([]);
    setWarnings([]);
    setYangoWeeks([]);
    setUnallocatedPos(0);
    setUnallocatedExpenses({ enocExpense: 0, trafficFineExpense: 0 });
    setNeedsRegenerate(true);
  };

  const changeReportBy = (value) => {
    invalidateGeneratedReport();
    setReportBy(value);
  };

  const changeCompany = (value) => {
    invalidateGeneratedReport();
    setCompany(value);
  };

  const changeFromDate = (value) => {
    invalidateGeneratedReport();
    setFromDate(value);
  };

  const changeToDate = (value) => {
    invalidateGeneratedReport();
    setToDate(value);
  };

  const setPreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    invalidateGeneratedReport();
    setFromDate(toIsoDate(start));
    setToDate(toIsoDate(end));
  };

  async function generate() {
    if (!company || !fromDate || !toDate) return alert("Select company and date range.");
    if (toDate < fromDate) return alert("To date cannot be before From date.");

    setLoading(true);
    try {
      const [assignmentsMap, vehicleMap, driverMasterMap] = await Promise.all([
        loadAssignmentsMap(),
        loadVehicleMap(),
        loadDriverMasterMap(),
      ]);

      // "By Cars" means car ownership/assignment controls the report; therefore
      // load income from BOTH platform companies first, then filter by car company.
      // "By Platform Account" means the selected company on the Uber/Bolt/Yango/POS
      // source controls the report, regardless of which company's car was used.
      const sourceIncomeCompany = reportBy === "cars" ? ALL_INCOME_COMPANIES : company;
      const effectiveCarCompanyFilter = reportBy === "cars" ? company : "all";

      const [dailyRows, bookingPortalRows, posRows, yango, expenseRows] = await Promise.all([
        loadDailyRideHailing(fromDate, toDate, sourceIncomeCompany),
        loadDailyBookingPortal(fromDate, toDate, sourceIncomeCompany),
        loadPOS(fromDate, toDate, sourceIncomeCompany),
        loadYangoAllocated(fromDate, toDate, sourceIncomeCompany, assignmentsMap, vehicleMap, driverMasterMap),
        loadReportExpenses(fromDate, toDate),
      ]);

      const built = buildReport({
        dailyRows,
        bookingPortalRows,
        posRows,
        yangoRows: yango.rows,
        expenseRows,
        assignmentsMap,
        vehicleMap,
        driverMasterMap,
        carCompanyFilter: effectiveCarCompanyFilter,
        reportBy,
        startDate: fromDate,
        endDate: toDate,
      });

      setDriverRows(built.driverRows);
      setWarnings(built.warnings);
      setUnallocatedPos(built.unallocatedPos || 0);
      setUnallocatedExpenses(built.unallocatedExpenses || { enocExpense: 0, trafficFineExpense: 0 });
      setYangoWeeks(yango.allocatedWeeks || []);
      setNeedsRegenerate(false);
    } catch (err) {
      console.error(err);
      alert(`Could not generate income report: ${err?.message || err}`);
    } finally {
      setLoading(false);
    }
  }

  const hasUnallocatedExpenses = Object.values(unallocatedExpenses || {}).some((v) => Math.abs(Number(v || 0)) > 0.0001);

  async function exportExcel() {
    if (needsRegenerate) return alert("Filters changed. Generate the report again before exporting.");
    if (!driverRows.length && !unallocatedPos && !hasUnallocatedExpenses) return alert("Generate the report first.");

    const wb = new ExcelJS.Workbook();
    wb.creator = "Comfort Line Accounting";
    wb.created = new Date();

    const metaText = `Report: ${reportByLabel} | Company: ${selectedCompanyLabel} | Period: ${fromDate} to ${toDate}`;
    const rowFill = ["FFF4F9FF", "FFFFF8E7"];

    // -------- Detailed sheet --------
    const detailed = wb.addWorksheet("Driver Detail", {
      views: [{ state: "frozen", ySplit: 5 }],
      pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    });

    detailed.mergeCells("A1:O1");
    detailed.getCell("A1").value = "DAILY INCOME - DRIVER / PLATFORM DETAIL";
    detailed.getCell("A1").font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
    detailed.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF17365D" } };
    detailed.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
    detailed.getRow(1).height = 26;

    detailed.mergeCells("A2:O2");
    detailed.getCell("A2").value = metaText;
    detailed.getCell("A2").font = { italic: true, color: { argb: "FF475569" } };
    detailed.getCell("A2").alignment = { horizontal: "center" };

    detailed.mergeCells("A3:O3");
    detailed.getCell("A3").value = "Booking Portal Bank/Cash comes only from the separate Daily Booking Portal Income upload. POS remains the existing POS upload. Yango: W1=7th, W2=14th, W3=21st, W4=28th, W5=month-end. Car Sheets are unchanged.";
    detailed.getCell("A3").font = { size: 9, color: { argb: "FF7C2D12" } };
    detailed.getCell("A3").alignment = { horizontal: "center", wrapText: true };

    detailed.addRow([]);
    const detailHeader = detailed.addRow([
      "Driver",
      "Cars Used",
      "Uber Bank",
      "Uber Cash",
      "Bolt Bank",
      "Bolt Cash",
      "Yango Bank",
      "Yango Cash",
      "Booking Portal Bank",
      "Booking Portal Cash",
      "POS Income",
      "Total Bank Income",
      "Remaining Cash With Driver",
      "ENOC Expense",
      "Traffic Fines",
    ]);
    excelApplyHeader(detailHeader);

    const detailStartRow = detailHeader.number + 1;
    driverRows.forEach((d, index) => {
      const row = detailed.addRow([
        d.driverName,
        d.carsUsed,
        n2(d.uberBank),
        n2(d.uberCash),
        n2(d.boltBank),
        n2(d.boltCash),
        n2(d.yangoBank),
        n2(d.yangoCash),
        n2(d.bookingPortalBank),
        n2(d.bookingPortalCash),
        n2(d.pos),
        n2(d.totalBank),
        n2(d.remainingCash),
        n2(d.enocExpense),
        n2(d.trafficFineExpense),
      ]);
      excelApplyDataRow(row, rowFill[(d.displayGroupIndex ?? index) % 2]);
      row.getCell(1).font = { bold: true };
      row.getCell(12).font = { bold: true };
      row.getCell(13).font = { bold: true };
    });

    if (unallocatedPos > 0) {
      const row = detailed.addRow(["POS NEEDS REVIEW", "See warnings", "", "", "", "", "", "", "", "", n2(unallocatedPos), "", "", "", ""]);
      row.font = { bold: true, color: { argb: "FF991B1B" } };
      row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEE2E2" } };
    }

    if (hasUnallocatedExpenses) {
      const row = detailed.addRow([
        "EXPENSE NEEDS REVIEW",
        "See warnings",
        "", "", "", "", "", "", "", "", "", "", "",
        n2(unallocatedExpenses.enocExpense),
        n2(unallocatedExpenses.trafficFineExpense),
      ]);
      row.font = { bold: true, color: { argb: "FF991B1B" } };
      row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEE2E2" } };
    }

    const detailTotal = detailed.addRow([
      "REPORT TOTAL",
      "",
      n2(detailTotals.uberBank),
      n2(detailTotals.uberCash),
      n2(detailTotals.boltBank),
      n2(detailTotals.boltCash),
      n2(detailTotals.yangoBank),
      n2(detailTotals.yangoCash),
      n2(detailTotals.bookingPortalBank),
      n2(detailTotals.bookingPortalCash),
      n2(detailTotals.pos),
      n2(detailTotals.totalBank),
      n2(detailTotals.remainingCash),
      n2(detailTotals.enocExpense),
      n2(detailTotals.trafficFineExpense),
    ]);
    detailTotal.font = { bold: true };
    detailTotal.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9EAF7" } };

    detailed.autoFilter = {
      from: { row: detailHeader.number, column: 1 },
      to: { row: Math.max(detailHeader.number + 1, detailTotal.number - 1), column: 15 },
    };

    detailed.columns = [
      { width: 32 }, { width: 58 },
      { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 },
      { width: 14 }, { width: 14 }, { width: 20 }, { width: 20 },
      { width: 16 }, { width: 19 }, { width: 26 }, { width: 16 }, { width: 16 },
    ];

    for (let r = detailStartRow; r <= detailed.rowCount; r += 1) {
      detailed.getRow(r).getCell(2).alignment = { vertical: "middle", wrapText: true };
      for (let c = 3; c <= 15; c += 1) {
        const cell = detailed.getRow(r).getCell(c);
        if (typeof cell.value === "number") cell.numFmt = "#,##0.00";
        cell.alignment = { vertical: "middle", horizontal: "right" };
      }
    }

    // -------- Summary sheet --------
    const summary = wb.addWorksheet("Summary", {
      views: [{ state: "frozen", ySplit: 4 }],
      pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    });

    summary.mergeCells("A1:L1");
    summary.getCell("A1").value = "DAILY INCOME - SUMMARY";
    summary.getCell("A1").font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
    summary.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF17365D" } };
    summary.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
    summary.getRow(1).height = 26;

    summary.mergeCells("A2:L2");
    summary.getCell("A2").value = metaText;
    summary.getCell("A2").font = { italic: true, color: { argb: "FF475569" } };
    summary.getCell("A2").alignment = { horizontal: "center" };
    summary.addRow([]);

    const summaryHeader = summary.addRow([
      "Driver",
      "Cars Used",
      "Ride-Hailing Bank Income",
      "Booking Portal Bank",
      "POS Income",
      "Total Bank Income",
      "Ride-Hailing Cash Collected",
      "Booking Portal Cash",
      "Total Cash Collected",
      "Remaining Cash With Driver",
      "ENOC Expense",
      "Traffic Fines",
    ]);
    excelApplyHeader(summaryHeader);

    driverRows.forEach((d, index) => {
      const row = summary.addRow([
        d.driverName,
        d.carsUsed,
        n2(d.rideHailingBank),
        n2(d.bookingPortalBank),
        n2(d.pos),
        n2(d.totalBank),
        n2(d.rideHailingCash),
        n2(d.bookingPortalCash),
        n2(d.totalCashCollected),
        n2(d.remainingCash),
        n2(d.enocExpense),
        n2(d.trafficFineExpense),
      ]);
      excelApplyDataRow(row, rowFill[(d.displayGroupIndex ?? index) % 2]);
      row.getCell(1).font = { bold: true };
      row.getCell(6).font = { bold: true };
      row.getCell(10).font = { bold: true };
    });

    if (unallocatedPos > 0) {
      const row = summary.addRow(["POS NEEDS REVIEW", "See warnings", "", "", n2(unallocatedPos), "", "", "", "", "", "", ""]);
      row.font = { bold: true, color: { argb: "FF991B1B" } };
      row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEE2E2" } };
    }

    if (hasUnallocatedExpenses) {
      const row = summary.addRow([
        "EXPENSE NEEDS REVIEW", "See warnings", "", "", "", "", "", "", "", "",
        n2(unallocatedExpenses.enocExpense),
        n2(unallocatedExpenses.trafficFineExpense),
      ]);
      row.font = { bold: true, color: { argb: "FF991B1B" } };
      row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEE2E2" } };
    }

    const grand = summary.addRow([
      "REPORT TOTAL",
      "",
      n2(detailTotals.rideHailingBank),
      n2(detailTotals.bookingPortalBank),
      n2(detailTotals.pos),
      n2(detailTotals.totalBank),
      n2(detailTotals.rideHailingCash),
      n2(detailTotals.bookingPortalCash),
      n2(detailTotals.totalCashCollected),
      n2(detailTotals.remainingCash),
      n2(detailTotals.enocExpense),
      n2(detailTotals.trafficFineExpense),
    ]);
    grand.font = { bold: true };
    grand.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9EAF7" } };

    summary.autoFilter = {
      from: { row: summaryHeader.number, column: 1 },
      to: { row: Math.max(summaryHeader.number + 1, grand.number - 1), column: 12 },
    };
    summary.columns = [
      { width: 32 }, { width: 58 }, { width: 24 }, { width: 21 },
      { width: 16 }, { width: 21 }, { width: 25 }, { width: 21 },
      { width: 22 }, { width: 27 }, { width: 16 }, { width: 16 },
    ];

    for (let r = summaryHeader.number + 1; r <= summary.rowCount; r += 1) {
      summary.getRow(r).getCell(2).alignment = { vertical: "middle", wrapText: true };
      for (let c = 3; c <= 12; c += 1) {
        const cell = summary.getRow(r).getCell(c);
        if (typeof cell.value === "number") cell.numFmt = "#,##0.00";
        cell.alignment = { vertical: "middle", horizontal: "right" };
      }
    }

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      safeFileName(`Income_${selectedCompanyLabel}_${reportBy === "cars" ? "Company_Cars" : "Platform_Account"}_${fromDate}_to_${toDate}.xlsx`)
    );
  }

  function exportPDF() {
    if (needsRegenerate) return alert("Filters changed. Generate the report again before exporting.");
    if (!driverRows.length && !unallocatedPos && !hasUnallocatedExpenses) return alert("Generate the report first.");

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const metaLine = `Report: ${reportByLabel} | Company: ${selectedCompanyLabel} | Period: ${fromDate} to ${toDate}`;

    const drawReportHeader = (title) => {
      doc.setFillColor(23, 54, 93);
      doc.rect(0, 0, pageW, 18, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(title, 10, 12);
      doc.setTextColor(35, 35, 35);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.text(metaLine, 10, 25);
    };

    drawReportHeader("Daily Income - Summary");
    const summaryRows = driverRows.map((d, index) => ({
      index: d.displayGroupIndex ?? index,
      values: [
        d.driverName,
        d.carsUsed,
        money(d.rideHailingBank),
        money(d.bookingPortalBank),
        money(d.pos),
        money(d.totalBank),
        money(d.rideHailingCash),
        money(d.bookingPortalCash),
        money(d.totalCashCollected),
        money(d.remainingCash),
        money(d.enocExpense),
        money(d.trafficFineExpense),
      ],
    }));

    if (unallocatedPos > 0) {
      summaryRows.push({
        index: -1,
        warning: true,
        values: ["POS NEEDS REVIEW", "See warning above / on screen", "", "", money(unallocatedPos), "", "", "", "", "", "", ""],
      });
    }

    if (hasUnallocatedExpenses) {
      summaryRows.push({
        index: -1,
        warning: true,
        values: [
          "EXPENSE NEEDS REVIEW", "See warnings", "", "", "", "", "", "", "", "",
          money(unallocatedExpenses.enocExpense),
          money(unallocatedExpenses.trafficFineExpense),
        ],
      });
    }

    summaryRows.push({
      index: -1,
      total: true,
      values: [
        "REPORT TOTAL",
        "",
        money(detailTotals.rideHailingBank),
        money(detailTotals.bookingPortalBank),
        money(detailTotals.pos),
        money(detailTotals.totalBank),
        money(detailTotals.rideHailingCash),
        money(detailTotals.bookingPortalCash),
        money(detailTotals.totalCashCollected),
        money(detailTotals.remainingCash),
        money(detailTotals.enocExpense),
        money(detailTotals.trafficFineExpense),
      ],
    });

    autoTable(doc, {
      startY: 30,
      margin: { left: 5, right: 5, bottom: 12 },
      theme: "grid",
      head: [[
        "Driver",
        "Cars Used",
        "Ride-Hailing Bank",
        "Booking Portal Bank",
        "POS",
        "Total Bank",
        "Ride-Hailing Cash",
        "Booking Portal Cash",
        "Total Cash",
        "Remaining Cash",
        "ENOC",
        "Traffic Fines",
      ]],
      body: summaryRows.map((r) => r.values),
      styles: { fontSize: 6.2, cellPadding: 1.1, valign: "middle", overflow: "linebreak" },
      headStyles: { fillColor: [31, 78, 120], textColor: 255, fontStyle: "bold", fontSize: 6.1 },
      columnStyles: {
        0: { cellWidth: 29 },
        1: { cellWidth: 36 },
        2: { cellWidth: 21, halign: "right" },
        3: { cellWidth: 20, halign: "right" },
        4: { cellWidth: 14, halign: "right" },
        5: { cellWidth: 18, halign: "right" },
        6: { cellWidth: 21, halign: "right" },
        7: { cellWidth: 20, halign: "right" },
        8: { cellWidth: 18, halign: "right" },
        9: { cellWidth: 22, halign: "right" },
        10: { cellWidth: 15, halign: "right" },
        11: { cellWidth: 16, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const item = summaryRows[data.row.index];
        if (item?.total) {
          data.cell.styles.fillColor = [217, 234, 247];
          data.cell.styles.fontStyle = "bold";
        } else if (item?.warning) {
          data.cell.styles.fillColor = [254, 226, 226];
          data.cell.styles.textColor = [153, 27, 27];
          data.cell.styles.fontStyle = "bold";
        } else if (item) {
          data.cell.styles.fillColor = item.index % 2 === 0 ? [244, 249, 255] : [255, 248, 231];
        }
      },
    });

    doc.addPage();
    drawReportHeader("Daily Income - Driver / Platform Detail");
    doc.setFontSize(7.2);
    doc.setTextColor(100, 70, 30);
    doc.text("Booking Portal Bank/Cash is from the separate daily management upload only. POS and Yango allocation rules remain management-report rules; Car Sheets are unchanged.", 10, 30);

    const detailPdfRows = driverRows.map((d, index) => ({
      index: d.displayGroupIndex ?? index,
      values: [
        d.driverName,
        d.carsUsed,
        money(d.uberBank),
        money(d.uberCash),
        money(d.boltBank),
        money(d.boltCash),
        money(d.yangoBank),
        money(d.yangoCash),
        money(d.bookingPortalBank),
        money(d.bookingPortalCash),
        money(d.pos),
        money(d.totalBank),
        money(d.remainingCash),
        money(d.enocExpense),
        money(d.trafficFineExpense),
      ],
    }));

    if (unallocatedPos > 0) {
      detailPdfRows.push({
        index: -1,
        warning: true,
        values: ["POS NEEDS REVIEW", "See warnings", "", "", "", "", "", "", "", "", money(unallocatedPos), "", "", "", ""],
      });
    }

    if (hasUnallocatedExpenses) {
      detailPdfRows.push({
        index: -1,
        warning: true,
        values: [
          "EXPENSE NEEDS REVIEW", "See warnings", "", "", "", "", "", "", "", "", "", "", "",
          money(unallocatedExpenses.enocExpense),
          money(unallocatedExpenses.trafficFineExpense),
        ],
      });
    }

    detailPdfRows.push({
      index: -1,
      total: true,
      values: [
        "REPORT TOTAL",
        "",
        money(detailTotals.uberBank),
        money(detailTotals.uberCash),
        money(detailTotals.boltBank),
        money(detailTotals.boltCash),
        money(detailTotals.yangoBank),
        money(detailTotals.yangoCash),
        money(detailTotals.bookingPortalBank),
        money(detailTotals.bookingPortalCash),
        money(detailTotals.pos),
        money(detailTotals.totalBank),
        money(detailTotals.remainingCash),
        money(detailTotals.enocExpense),
        money(detailTotals.trafficFineExpense),
      ],
    });

    autoTable(doc, {
      startY: 34,
      margin: { left: 4, right: 4, bottom: 12 },
      theme: "grid",
      head: [[
        "Driver",
        "Cars Used",
        "Uber Bank",
        "Uber Cash",
        "Bolt Bank",
        "Bolt Cash",
        "Yango Bank",
        "Yango Cash",
        "Booking Portal Bank",
        "Booking Portal Cash",
        "POS",
        "Total Bank",
        "Remaining Cash",
        "ENOC",
        "Traffic Fines",
      ]],
      body: detailPdfRows.map((r) => r.values),
      styles: { fontSize: 5.2, cellPadding: 0.8, valign: "middle", overflow: "linebreak" },
      headStyles: { fillColor: [31, 78, 120], textColor: 255, fontStyle: "bold", fontSize: 5.1 },
      columnStyles: {
        0: { cellWidth: 26 },
        1: { cellWidth: 31 },
        2: { cellWidth: 12, halign: "right" },
        3: { cellWidth: 12, halign: "right" },
        4: { cellWidth: 12, halign: "right" },
        5: { cellWidth: 12, halign: "right" },
        6: { cellWidth: 12, halign: "right" },
        7: { cellWidth: 12, halign: "right" },
        8: { cellWidth: 18, halign: "right" },
        9: { cellWidth: 18, halign: "right" },
        10: { cellWidth: 12, halign: "right" },
        11: { cellWidth: 15, halign: "right" },
        12: { cellWidth: 18, halign: "right" },
        13: { cellWidth: 13, halign: "right" },
        14: { cellWidth: 14, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const item = detailPdfRows[data.row.index];
        if (item?.total) {
          data.cell.styles.fillColor = [217, 234, 247];
          data.cell.styles.fontStyle = "bold";
        } else if (item?.warning) {
          data.cell.styles.fillColor = [254, 226, 226];
          data.cell.styles.textColor = [153, 27, 27];
          data.cell.styles.fontStyle = "bold";
        } else if (item) {
          data.cell.styles.fillColor = item.index % 2 === 0 ? [244, 249, 255] : [255, 248, 231];
        }
      },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(110, 110, 110);
      doc.text(`Page ${i} of ${pageCount}`, pageW / 2, doc.internal.pageSize.getHeight() - 5, { align: "center" });
    }

    doc.save(safeFileName(`Income_${selectedCompanyLabel}_${reportBy === "cars" ? "Company_Cars" : "Platform_Account"}_${fromDate}_to_${toDate}.pdf`));
  }

  return (
    <div style={{ padding: 22 }}>
      <h2 style={{ marginTop: 0 }}>Daily Income Report</h2>
      <p style={{ color: "#475569", maxWidth: 1180 }}>
        One row is shown per driver. If a driver used several cars during the selected period, all of that driver&apos;s income is combined and the cars are listed under <strong>Cars Used</strong>. Drivers who share the same car are kept together in the report and use the same row shading.
        Uber and Bolt come from the separate daily uploads. Booking Portal Bank/Cash comes from the separate Daily Booking Portal Income upload. Yango and POS are read from the existing uploads. ENOC/Petrol and uploaded Traffic Fines are shown as separate expense columns. All daily Booking Portal data is management-report-only and does not post anything to Car Sheets, monthly Manual Booking income, or client invoices.
      </p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end", marginBottom: 14 }}>
        <label style={fieldLabel}>Report Type
          <select value={reportBy} onChange={(e) => changeReportBy(e.target.value)} style={{ ...input, minWidth: 210 }}>
            <option value="cars">By Company Cars</option>
            <option value="account">By Platform Account</option>
          </select>
        </label>

        <label style={fieldLabel}>Company
          <select value={company} onChange={(e) => changeCompany(e.target.value)} style={{ ...input, minWidth: 210 }}>
            {COMPANIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>

        <label style={fieldLabel}>From
          <input type="date" value={fromDate} onChange={(e) => changeFromDate(e.target.value)} style={input} />
        </label>
        <label style={fieldLabel}>To
          <input type="date" value={toDate} onChange={(e) => changeToDate(e.target.value)} style={input} />
        </label>
        <button onClick={() => setPreset(1)} style={smallButton}>Today</button>
        <button onClick={() => setPreset(7)} style={smallButton}>Last 7 Days</button>
        <button onClick={() => setPreset(10)} style={smallButton}>Last 10 Days</button>
        <button onClick={generate} disabled={loading} style={button("#2563eb")}>{loading ? "Generating..." : "Generate Report"}</button>
        <button onClick={exportExcel} disabled={needsRegenerate || (!driverRows.length && !unallocatedPos && !hasUnallocatedExpenses)} style={button("#15803d")}>Export Excel</button>
        <button onClick={exportPDF} disabled={needsRegenerate || (!driverRows.length && !unallocatedPos && !hasUnallocatedExpenses)} style={button("#b91c1c")}>Export PDF</button>
      </div>

      {needsRegenerate && (
        <div style={{ padding: 10, background: "#fff7ed", border: "1px solid #fdba74", borderRadius: 8, marginBottom: 12, color: "#9a3412", fontWeight: 700 }}>
          Filters changed — click Generate Report before reviewing or exporting figures.
        </div>
      )}

      <div style={{ padding: 10, background: "#eef6ff", border: "1px solid #bfdbfe", borderRadius: 8, marginBottom: 12, color: "#1e3a8a", fontSize: 13 }}>
        <strong>{reportBy === "cars" ? "By Company Cars:" : "By Platform Account:"}</strong> {scopeDescription}
        <div style={{ marginTop: 5, color: "#334155" }}>
          {reportBy === "cars"
            ? "Use this when management asks: ‘Give me the Comfort cars report’ or ‘Give me the Lolia cars report’."
            : "Use this when you want to reconcile the Comfort or Lolia platform-account income itself."}
        </div>
      </div>

      <div style={{ padding: 12, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, marginBottom: 12 }}>
        <strong>POS allocation for this report only:</strong> if one driver is assigned to the car on the transaction date, POS goes to that driver. If assignments overlap, POS goes to the driver whose assignment continues furthest on that car. If the continuing driver cannot be determined, the POS stays unallocated and is flagged for review.
      </div>

      <div style={{ padding: 12, background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 8, marginBottom: 12 }}>
        <strong>Booking Portal:</strong> daily Bank/Cash income is matched to the saved driver and the driver&apos;s car assignment on that date. Booking Portal Bank is added to Total Bank Income. Booking Portal Cash is added to Total Cash Collected before POS is deducted to calculate Remaining Cash With Driver. This data is not used by Car Sheets.
      </div>

      <div style={{ padding: 12, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, marginBottom: 12 }}>
        <strong>Expense allocation:</strong> ENOC/Petrol and Traffic Fines are matched by car number and expense date, then allocated using the same active-car assignment logic as POS. Traffic Fine uploads are for this report only and do not change Car Sheets.
      </div>

      <div style={{ padding: 12, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, marginBottom: 16 }}>
        <strong>Yango allocation:</strong> existing Yango Week 1 is posted on the 7th, Week 2 on the 14th,
        Week 3 on the 21st, Week 4 on the 28th, and Week 5 on the last day of that month. This allocation is only for this report.
        {yangoWeeks.length > 0 && <div style={{ marginTop: 6 }}>Included: {yangoWeeks.join("; ")}</div>}
      </div>

      {warnings.length > 0 && (
        <div style={{ padding: 12, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, marginBottom: 16 }}>
          <strong>Check:</strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
            {warnings.map((w, i) => <li key={`${w}-${i}`}>{w}</li>)}
          </ul>
          {unallocatedPos > 0 && <div style={{ marginTop: 6, fontWeight: 800 }}>Unallocated POS: AED {money(unallocatedPos)}</div>}
        </div>
      )}

      <h3>Driver / Platform Detail</h3>
      <div style={{ overflowX: "auto" }}>
        <table style={table}>
          <thead>
            <tr>
              {[
                "Driver",
                "Cars Used",
                "Uber Bank",
                "Uber Cash",
                "Bolt Bank",
                "Bolt Cash",
                "Yango Bank",
                "Yango Cash",
                "Booking Portal Bank",
                "Booking Portal Cash",
                "POS Income",
                "Total Bank Income",
                "Remaining Cash With Driver",
                "ENOC Expense",
                "Traffic Fines",
              ].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {driverRows.map((d, index) => {
              const groupStyle = (d.displayGroupIndex ?? index) % 2 === 0 ? rowGroupA : rowGroupB;
              return (
                <tr key={d.driverId || d.driverName} style={groupStyle}>
                  <td style={{ ...td, ...groupStyle, fontWeight: 800 }}>{d.driverName}</td>
                  <td style={{ ...td, ...groupStyle, maxWidth: 360 }}>{d.carsUsed}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.uberBank)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.uberCash)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.boltBank)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.boltCash)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.yangoBank)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.yangoCash)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.bookingPortalBank)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.bookingPortalCash)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.pos)}</td>
                  <td style={{ ...num, ...groupStyle, fontWeight: 800 }}>{money(d.totalBank)}</td>
                  <td style={{ ...num, ...groupStyle, fontWeight: 800 }}>{money(d.remainingCash)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.enocExpense)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.trafficFineExpense)}</td>
                </tr>
              );
            })}
            {!driverRows.length && !unallocatedPos && <tr><td colSpan={15} style={{ ...td, textAlign: "center", color: "#64748b" }}>Generate a report to view data.</td></tr>}
            {unallocatedPos > 0 && (
              <tr style={{ background: "#fee2e2", color: "#991b1b", fontWeight: 800 }}>
                <td style={td}>POS NEEDS REVIEW</td><td style={td}>See warning above</td>
                <td style={num}></td><td style={num}></td><td style={num}></td><td style={num}></td>
                <td style={num}></td><td style={num}></td><td style={num}></td><td style={num}></td>
                <td style={num}>{money(unallocatedPos)}</td><td style={num}></td><td style={num}></td>
                <td style={num}></td><td style={num}></td>
              </tr>
            )}
            {hasUnallocatedExpenses && (
              <tr style={{ background: "#fee2e2", color: "#991b1b", fontWeight: 800 }}>
                <td style={td}>EXPENSE NEEDS REVIEW</td><td style={td}>See warning above</td>
                <td style={num}></td><td style={num}></td><td style={num}></td><td style={num}></td>
                <td style={num}></td><td style={num}></td><td style={num}></td><td style={num}></td>
                <td style={num}></td><td style={num}></td><td style={num}></td>
                <td style={num}>{money(unallocatedExpenses.enocExpense)}</td>
                <td style={num}>{money(unallocatedExpenses.trafficFineExpense)}</td>
              </tr>
            )}
            {(driverRows.length > 0 || unallocatedPos > 0 || hasUnallocatedExpenses) && (
              <tr style={{ background: "#d9eaf7", fontWeight: 900 }}>
                <td style={td}>COMPANY TOTAL</td><td style={td}></td>
                <td style={num}>{money(detailTotals.uberBank)}</td>
                <td style={num}>{money(detailTotals.uberCash)}</td>
                <td style={num}>{money(detailTotals.boltBank)}</td>
                <td style={num}>{money(detailTotals.boltCash)}</td>
                <td style={num}>{money(detailTotals.yangoBank)}</td>
                <td style={num}>{money(detailTotals.yangoCash)}</td>
                <td style={num}>{money(detailTotals.bookingPortalBank)}</td>
                <td style={num}>{money(detailTotals.bookingPortalCash)}</td>
                <td style={num}>{money(detailTotals.pos)}</td>
                <td style={num}>{money(detailTotals.totalBank)}</td>
                <td style={num}>{money(detailTotals.remainingCash)}</td>
                <td style={num}>{money(detailTotals.enocExpense)}</td>
                <td style={num}>{money(detailTotals.trafficFineExpense)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginTop: 28 }}>Summary</h3>
      <div style={{ overflowX: "auto" }}>
        <table style={tableSummary}>
          <thead>
            <tr>
              {[
                "Driver",
                "Cars Used",
                "Ride-Hailing Bank Income",
                "Booking Portal Bank",
                "POS Income",
                "Total Bank Income",
                "Ride-Hailing Cash Collected",
                "Booking Portal Cash",
                "Total Cash Collected",
                "Remaining Cash With Driver",
                "ENOC Expense",
                "Traffic Fines",
              ].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {driverRows.map((d, index) => {
              const groupStyle = (d.displayGroupIndex ?? index) % 2 === 0 ? rowGroupA : rowGroupB;
              return (
                <tr key={`summary-${d.driverId || d.driverName}`} style={groupStyle}>
                  <td style={{ ...td, ...groupStyle, fontWeight: 800 }}>{d.driverName}</td>
                  <td style={{ ...td, ...groupStyle, maxWidth: 360 }}>{d.carsUsed}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.rideHailingBank)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.bookingPortalBank)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.pos)}</td>
                  <td style={{ ...num, ...groupStyle, fontWeight: 800 }}>{money(d.totalBank)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.rideHailingCash)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.bookingPortalCash)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.totalCashCollected)}</td>
                  <td style={{ ...num, ...groupStyle, fontWeight: 800 }}>{money(d.remainingCash)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.enocExpense)}</td>
                  <td style={{ ...num, ...groupStyle }}>{money(d.trafficFineExpense)}</td>
                </tr>
              );
            })}
            {unallocatedPos > 0 && (
              <tr style={{ background: "#fee2e2", color: "#991b1b", fontWeight: 800 }}>
                <td style={td}>POS NEEDS REVIEW</td><td style={td}>See warning above</td>
                <td style={num}></td><td style={num}></td><td style={num}>{money(unallocatedPos)}</td>
                <td style={num}></td><td style={num}></td><td style={num}></td><td style={num}></td><td style={num}></td>
                <td style={num}></td><td style={num}></td>
              </tr>
            )}
            {hasUnallocatedExpenses && (
              <tr style={{ background: "#fee2e2", color: "#991b1b", fontWeight: 800 }}>
                <td style={td}>EXPENSE NEEDS REVIEW</td><td style={td}>See warning above</td>
                <td style={num}></td><td style={num}></td><td style={num}></td><td style={num}></td>
                <td style={num}></td><td style={num}></td><td style={num}></td><td style={num}></td>
                <td style={num}>{money(unallocatedExpenses.enocExpense)}</td>
                <td style={num}>{money(unallocatedExpenses.trafficFineExpense)}</td>
              </tr>
            )}
            {(driverRows.length > 0 || unallocatedPos > 0 || hasUnallocatedExpenses) && (
              <tr style={{ background: "#d9eaf7", fontWeight: 900 }}>
                <td style={td}>COMPANY TOTAL</td><td style={td}></td>
                <td style={num}>{money(detailTotals.rideHailingBank)}</td>
                <td style={num}>{money(detailTotals.bookingPortalBank)}</td>
                <td style={num}>{money(detailTotals.pos)}</td>
                <td style={num}>{money(detailTotals.totalBank)}</td>
                <td style={num}>{money(detailTotals.rideHailingCash)}</td>
                <td style={num}>{money(detailTotals.bookingPortalCash)}</td>
                <td style={num}>{money(detailTotals.totalCashCollected)}</td>
                <td style={num}>{money(detailTotals.remainingCash)}</td>
                <td style={num}>{money(detailTotals.enocExpense)}</td>
                <td style={num}>{money(detailTotals.trafficFineExpense)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const fieldLabel = { display: "flex", flexDirection: "column", gap: 5, fontWeight: 700 };
const input = { minWidth: 180, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, background: "white" };
const smallButton = { padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, background: "white", cursor: "pointer", fontWeight: 700 };
const button = (background) => ({ padding: "9px 14px", border: "none", borderRadius: 6, background, color: "white", fontWeight: 800, cursor: "pointer" });
const table = { borderCollapse: "collapse", width: "100%", minWidth: 1740, background: "white" };
const tableSummary = { borderCollapse: "collapse", width: "100%", minWidth: 1510, background: "white" };
const th = { padding: 9, border: "1px solid #cbd5e1", background: "#1f4e78", color: "white", textAlign: "left", whiteSpace: "nowrap" };
const td = { padding: 8, border: "1px solid #d5dee7", verticalAlign: "middle" };
const num = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const rowGroupA = { background: "#f4f9ff" };
const rowGroupB = { background: "#fff8e7" };
